import { useEffect, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { PageNav, PageFooter } from "@/components/blog/PageChrome";
import { AuthForm } from "@/components/admin/AuthForm";
import { MessageCard } from "@/components/admin/MessageCard";
import { useAdminInbox } from "@/hooks/useAdminInbox";

export default function AdminMessages() {
  const { token, messages, loading, error, copiedId, fetchMessages, login, logout, copy } = useAdminInbox();
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Admin · Messages — Himanshu Oberoi";
  }, []);

  const filtered = messages.filter((m) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      m.name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.company?.toLowerCase().includes(q) ||
      m.message?.toLowerCase().includes(q)
    );
  });

  if (!token) {
    return (
      <main className="relative min-h-screen bg-obsidian text-white overflow-x-hidden" data-testid="admin-root">
        <div className="grain" aria-hidden />
        <PageNav />
        <section className="container-x pt-40 pb-32 flex items-center justify-center">
          <AuthForm error={error} onSubmit={login} />
        </section>
        <PageFooter />
      </main>
    );
  }

  return (
    <main className="relative min-h-screen bg-obsidian text-white overflow-x-hidden" data-testid="admin-root">
      <div className="grain" aria-hidden />
      <PageNav />

      <section className="container-x pt-32 md:pt-40 pb-16" data-testid="admin-hero">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow">/// Admin · Inbox</p>
            <h1 className="mt-6 font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink">
              Contact submissions.
            </h1>
            <p className="mt-4 font-mono text-[11px] tracking-[0.24em] uppercase text-steel" data-testid="admin-count">
              {messages.length} total · {filtered.length} shown
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:justify-self-end flex gap-3">
            <button
              onClick={() => fetchMessages(token)}
              disabled={loading}
              data-testid="admin-refresh"
              className="btn-sweep inline-flex items-center gap-2 border border-stroke text-ink px-4 py-3 font-mono text-[11px] tracking-[0.24em] uppercase disabled:opacity-50"
            >
              <RefreshCw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-sweep inline-flex items-center gap-2 border border-stroke text-ink px-4 py-3 font-mono text-[11px] tracking-[0.24em] uppercase"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Logout
            </button>
          </div>
        </div>

        <div className="mt-10">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, company, message…"
            data-testid="admin-search"
            className="w-full bg-matte border border-stroke text-ink px-4 py-3 font-sans text-sm focus:outline-none focus:border-laser transition-colors"
          />
        </div>
      </section>

      <section className="container-x pb-24 md:pb-40" data-testid="admin-list">
        {error && (
          <div className="border border-red-500/50 bg-red-500/10 p-6 mb-6 font-mono text-sm text-red-300" data-testid="admin-error">
            {error}
          </div>
        )}
        {!error && filtered.length === 0 && !loading && (
          <div className="border border-stroke bg-matte p-10 text-center" data-testid="admin-empty">
            <p className="font-serif font-light text-2xl text-ink">No messages{query ? " match your search" : " yet"}.</p>
          </div>
        )}

        <div className="space-y-4">
          {filtered.map((m, i) => (
            <MessageCard key={m.id} message={m} index={i} copied={copiedId === m.id} onCopy={copy} />
          ))}
        </div>
      </section>

      <PageFooter />
    </main>
  );
}
